import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ListingStayDetailHeader } from "./components/ListingStayDetailHeader";
import { ListingStayDetailTitle } from "./components/ListingStayDetailTitle";
import { ListingStayDetailDescription } from "./components/ListingStayDetailDescription";
import MobileFooterSticky from "../(components)/MobileFooterSticky";
import { ListingStayDetailAmenities } from "./components/ListingStayDetailAmenities";
import { ListingStayDetailRooms } from "./components/ListingStayDetailRooms";
import { Room, RootHotel } from "./type";
import { ListingStayDetailSidebar } from "./components/ListingStayDetailSidebar";
import { fetchHotelDetails } from "services/fetch-hotel-details";
import { existLetter } from "utils/exist-letter";
import { NotFoundHotel } from "./components/NotFoundHotel";
import { BookingCondition } from "./components/type";
import { ReservationForm } from "./ReservationForm";
import { useHotels } from "contexts/HotelsSearchContext/HotelProvider";
import toast from "react-hot-toast";
import { useMetadataUser } from "contexts/metadata-user-context";

type RoomSelected = Room & {
  RoomSelected: number;
};

export function ListingStayDetailTemplate() {
  const [roomSelected, setRoomSelected] = useState<RoomSelected | null>(null);
  const [hotel, setHotel] = useState<RootHotel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [conditionsDataGlobal, setConditionsDataGlobal] =
    useState<BookingCondition | null>(null);
  const { profile } = useMetadataUser();

  const { isBooking, numNights, guests } = useHotels();

  const handleRoomSelected = (room: unknown) => {
    setRoomSelected(room as RoomSelected);
  };

  const params = useParams();

  const destinationId = params.destination_id!;
  const hotelId = params.hotel_id!;

  useEffect(() => {
    const execute = async () => {
      try {
        if (!destinationId || !hotelId) {
          throw new Error("destinationId or hotelId is not defined");
        }

        if (existLetter(destinationId) || existLetter(hotelId)) {
          throw new Error("destinationId or hotelId is only numbers");
        }

        const data = await fetchHotelDetails(
          destinationId,
          hotelId,
          searchParams.get("checkin") ?? "",
          Number(searchParams.get("nights")) ?? "",
          Number(searchParams.get("adults")) ?? "",
          Number(searchParams.get("children")) ?? undefined,
          searchParams.get("ages_children")
            ? searchParams.get("ages_children")?.split(",").map(Number)
            : undefined
        );
        setHotel(data);
      } catch (error) {
        setHotel(null);
      } finally {
        setIsLoading(false);
      }
    };

    execute();
  }, [destinationId, hotelId, numNights, guests, searchParams]);

  useEffect(() => {
    if (hotel) {
      const selected = [...hotel.rooms].map((room, index) => ({
        ...room,
        RoomSelected: index === 0 ? 1 : 0,
      }));

      setRoomSelected(selected[0]);
    }
  }, [hotel]);

  if (!hotel) {
    return <NotFoundHotel isLoading={isLoading} />;
  }

  const gallery = hotel.photos.map((photo, index) => {
    return {
      id: index,
      url: photo,
    };
  });

  if (isBooking && !conditionsDataGlobal) {
    toast.error(
      "Tivemos um problema ao buscar as condições de reserva. Tente novamente mais tarde."
    );
  }

  if (!profile) return null;

  return (
    <div className="ListingDetailPage mt-8 px-2" id="topo">
      <ListingStayDetailHeader photos={gallery} />

      <div className="container">
        <main
          className={`${
            isBooking
              ? "relative z-10 mt-11 flex flex-col justify-center items-center mx-auto pb-10"
              : "relative z-10 mt-11 flex flex-col lg:flex-row pb-10"
          }
        `}
        >
          <div
            className={`${
              isBooking
                ? "w-full lg:w-3/5 xl:w-4/5 space-y-8 lg:space-y-10 lg:pr-10"
                : "w-full lg:w-3/5 xl:w-2/3 space-y-8 lg:space-y-10 lg:pr-10"
            }`}
          >
            <ListingStayDetailTitle
              destination={hotel.destination.name}
              name={hotel.name}
              rating={hotel.category}
            />
            <ListingStayDetailDescription description={hotel.description} />

            {hotel.amenities ? (
              <ListingStayDetailAmenities amenities={hotel.amenities} />
            ) : (
              <div className="listingSection__wrap flex items-center justify-center">
                <span className="text-red-400">Não há comodidades</span>
              </div>
            )}

            <ListingStayDetailRooms
              rooms={hotel.rooms}
              handleRoomSelected={handleRoomSelected}
              profile={profile}
            />

            {isBooking && !!conditionsDataGlobal ? (
              <ReservationForm
                hotelId={+hotelId}
                data={conditionsDataGlobal}
                room={roomSelected}
                profile={profile}
                cashback={hotel.rooms.find(
                  (room) => room.id === roomSelected?.id
                )?.cashback.value}
              />
            ) : null}
          </div>

          <div
            className={`${
              isBooking ? "hidden" : "hidden lg:block flex-grow mt-14 lg:mt-0"
            }`}
          >
            <div className="sticky top-28">
              <ListingStayDetailSidebar
                room={roomSelected}
                rating={hotel.category}
                hotelId={hotel.id}
                token={searchParams.get("token")!}
                profile={profile}
                setConditionsDataGlobal={setConditionsDataGlobal}
                checkInDate={searchParams.get("checkin")!}
              />
            </div>
          </div>
        </main>
      </div>

      {!isBooking && (
        <MobileFooterSticky
          hotelId={hotel.id}
          token={searchParams.get("token")!}
          room={roomSelected}
          profile={profile}
          setConditionsDataGlobal={setConditionsDataGlobal}
          checkin={searchParams.get("checkin")!}
          numNights={Number(searchParams.get("nights")!)}
          adults={Number(searchParams.get("adults")!)}
          childrenAgeQuantity={Number(searchParams.get("children")!)}
        />
      )}
    </div>
  );
}
